import { HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, Observable, of, tap } from 'rxjs';
import { Store } from './store';
import { addDays, localMidnight } from '../utils/date-only';
import { downloadBlob } from '../utils/download';
import { ReportsProvider } from '../components/pages/reports/reports.provider';
import { MaterialRequirementsProvider } from '../components/pages/material-requirements/material-requirements.provider';

export type ReportExportStatus = 'running' | 'ready' | 'failed';

/** Which Raporty tab the file is of: the progress log (0106) or Zapotrzebowanie materiałowe (0118). */
export type ReportExportKind = 'progress' | 'materials';

export interface ReportExport {
  id: number;
  kind: ReportExportKind;
  /** The calendar days the file covers, as the Raporty page showed them ("2026-10-01"). */
  from: string;
  to: string;
  status: ReportExportStatus;
  /** Both set once the API has answered: the name it chose and the workbook itself. */
  fileName: string | undefined;
  blob: Blob | undefined;
  /** Set a few seconds after Pobierz: the card is fading out and is dismissed when the fade ends. */
  closing: boolean;
}

export interface ReportExportsState {
  /** In the order they were started; the tray draws them top to bottom. */
  exports: ReportExport[];
}

const INITIAL_STATE: ReportExportsState = {
  exports: []
};

// Every id is unique for the life of the tab, so a dismissed card never shares one with the next
let nextId = 1;

// How long a downloaded card stays, and how long it fades — the latter is the tray's .closing transition
const CLOSE_AFTER_DOWNLOAD_MS = 3000;
const FADE_MS = 400;

// The Excel exports in flight and the files that arrived (0107). A sibling of the session store:
// root-provided and never destroyed by a page, so a request started on Raporty keeps running
// while the user opens a zlecenie, and the file waits in the tray until it is dismissed. A
// full reload (signing out, a lost session) drops it all with the rest of the tab
@Injectable({ providedIn: 'root' })
export class ReportExportsStore extends Store<ReportExportsState> {
  constructor(
    private reportsProvider: ReportsProvider,
    private materialRequirementsProvider: MaterialRequirementsProvider
  ) {
    super(INITIAL_STATE);
  }

  start(kind: ReportExportKind, range: { from: string; to: string }): void {
    const id = nextId++;

    this.update(state => ({
      ...state,
      exports: [...state.exports, { id, kind, ...range, status: 'running', fileName: undefined, blob: undefined, closing: false }]
    }));

    this.run(id);
  }

  // Pobierz is what the card waited for, so the card fades out a few seconds later and the blob
  // goes with it. Until then it can be saved again; a second click's timers find the card already
  // closing or gone and change nothing
  download(id: number): void {
    const entry = this.find(id);

    if (entry?.status !== 'ready') {
      return;
    }

    downloadBlob(entry.blob!, entry.fileName!);

    setTimeout(() => this.update(patch(id, { closing: true })), CLOSE_AFTER_DOWNLOAD_MS);
    setTimeout(() => this.dismiss(id), CLOSE_AFTER_DOWNLOAD_MS + FADE_MS);
  }

  retry(id: number): void {
    if (this.find(id)?.status !== 'failed') {
      return;
    }

    this.update(patch(id, { status: 'running' }));
    this.run(id);
  }

  dismiss(id: number): void {
    this.update(state => ({ ...state, exports: state.exports.filter(entry => entry.id !== id) }));
  }

  // The store subscribes itself: the request belongs to no page, so no page's destroy may
  // cancel it
  private run(id: number): void {
    const entry = this.find(id)!;

    this.request(entry)
      .pipe(
        tap(response => this.update(patch(id, { status: 'ready', fileName: fileNameOf(response, entry.kind), blob: response.body! }))),
        // a 401 lands here too, but the interceptor is already reloading the document
        catchError(() => {
          this.update(patch(id, { status: 'failed' }));
          return of(undefined);
        })
      )
      .subscribe();
  }

  // The progress log sends the same two instants and zone 0106's Raporty store sent — the API
  // knows no time zone, so two calendar days become [local 00:00 of od, local 00:00 of the day
  // after do). Zapotrzebowanie sends the days themselves (planned windows are days); its zone
  // only stamps the file
  private request(entry: ReportExport): Observable<HttpResponse<Blob>> {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    return entry.kind === 'materials'
      ? this.materialRequirementsProvider.exportMaterialRequirements(entry.from, entry.to, timeZone)
      : this.reportsProvider.exportReports(localMidnight(entry.from), localMidnight(addDays(entry.to, 1)), timeZone);
  }

  private find(id: number): ReportExport | undefined {
    return this.state.exports.find(entry => entry.id === id);
  }
}

// one entry changed, the rest kept; a dismissed id changes nothing
function patch(id: number, changes: Partial<ReportExport>) {
  return (state: ReportExportsState): ReportExportsState => ({
    ...state,
    exports: state.exports.map(entry => entry.id === id ? { ...entry, ...changes } : entry)
  });
}

// The name the API chose, from `attachment; filename=raporty_…xlsx; filename*=…`
function fileNameOf(response: HttpResponse<Blob>, kind: ReportExportKind): string {
  const match = /filename="?([^";]+)"?/.exec(response.headers.get('Content-Disposition') ?? '');

  return match?.[1] ?? (kind === 'materials' ? 'zapotrzebowanie.xlsx' : 'raporty.xlsx');
}
